// ============================================================================
// Second degré, produits et quotients (moteur v23, refonte lot 5.2 —
// docs/moteur/REFONTE_MOTEUR.md). Fonctions PURES, sans état de feuille.
// ============================================================================
// Dans une feuille `kernel.method = 'factor'`, on n'isole pas l'inconnue d'une
// équation du second degré : on mesure l'avancée à la FORME de la ligne écrite.
//
//   transcendante (x sous exp, ln…)        40 + isolation
//   rationnelle (x au dénominateur, « = ») 30   chasser le dénominateur
//   deux membres non nuls (x² ≤ 3x)        25   tout passer du même côté
//   développée face à 0 (x² − 5x + 6 ≥ 0)  20   factoriser, ou Δ et racines
//   partiellement factorisée               17
//   canonique (x² − 4 ≤ 0, (x − 2)² − 9)   15   isoler le carré, ou a² − b²
//   un carré face à une constante          10   racine carrée
//   un cube face à une constante           10   fonction cube (moteur v41)
//   factorisée face à 0                    10   produit nul, tableau de signes
//   premier degré                          isolation habituelle (< 10)
//   x seul face à une constante             0
//
// Un ENSEMBLE (« x − 2 = 0 ou x − 3 = 0 », « −3 < x − 2 < 3 ») vaut sa clause la
// plus éloignée, plus un dixième des autres : résoudre une clause avance.
// ============================================================================

import * as Q from './kernel/exact';
import type { Rat } from './kernel/exact';
import {
  normalize as normalForm,
  polyIsZero,
  polyRat,
  rfSimplify,
  type Poly,
  type RatFn,
} from './kernel/normal';
import { sub, symbolsOf, type Rel, type Relation, type Statement, type Term } from './kernel/term';
import { univariateCoefs } from './kernel/witness';

export type PolyForm =
  | 'solved'
  | 'linear'
  | 'square'
  | 'cube'
  | 'factored'
  | 'canonical'
  | 'partial'
  | 'expanded'
  | 'unsorted'
  | 'rational'
  | 'transcendental'
  | 'unknown';

/** Valeur de la forme (hors premier degré, mesuré par l'isolation habituelle). */
export const FORM_H: Record<Exclude<PolyForm, 'linear'>, number> = {
  solved: 0,
  square: 10,
  cube: 10,
  factored: 10,
  canonical: 15,
  partial: 17,
  expanded: 20,
  unsorted: 25,
  rational: 30,
  transcendental: 40,
  unknown: 50,
};

const has = (t: Term, x: string): boolean => symbolsOf(t).has(x);

function occurrences(t: Term, x: string): number {
  switch (t.k) {
    case 'sym':
      return t.n === x ? 1 : 0;
    case 'num':
      return 0;
    case 'add':
    case 'mul':
      return t.c.reduce((s, c) => s + occurrences(c, x), 0);
    case 'div':
    case 'pow':
    case 'binom':
      return occurrences(t.a, x) + occurrences(t.b, x);
    case 'fn':
      return occurrences(t.a, x);
  }
}

/** forme réduite (fraction rationnelle simplifiée) ; null si le calcul échoue */
function reducedRf(t: Term): RatFn | null {
  const n = normalForm(t);
  return n.ok ? rfSimplify(n.rf) : null;
}

export interface Shape {
  /** degré en x du numérateur réduit */
  deg: number;
  /** x au dénominateur réduit */
  den: boolean;
  /** x sous une fonction (exp, ln, √…) : pas un polynôme */
  opaque: boolean;
}

/** Degré en x de la forme réduite d'un terme ; null si le calcul échoue. */
export function shapeOf(t: Term, x: string): Shape | null {
  const rf = reducedRf(t);
  if (!rf) return null;
  const scan = (p: Poly): { deg: number; opaque: boolean } => {
    let deg = 0;
    let opaque = false;
    for (const { m } of p.t.values())
      for (const f of m) {
        if (f.a.kind === 'sym' && f.a.symbol === x) deg = Math.max(deg, f.e);
        else if (f.a.kind === 'opaque' && !f.a.constant && has(f.a.term, x)) opaque = true;
      }
    return { deg, opaque };
  };
  const n = scan(rf.n);
  const d = scan(rf.d);
  // un dénominateur ÉCRIT qui contient x compte, même s'il se simplifie
  // (« (x² − 4) ÷ (x − 2) » : la valeur interdite x = 2 reste à écarter)
  return {
    deg: n.deg,
    den: d.deg > 0 || d.opaque || writtenDen(t, x),
    opaque: n.opaque || d.opaque,
  };
}

/** x apparaît-il dans un dénominateur écrit ? */
export function writtenDen(t: Term, x: string): boolean {
  switch (t.k) {
    case 'add':
    case 'mul':
      return t.c.some((c) => writtenDen(c, x));
    case 'div':
      return has(t.b, x) || writtenDen(t.a, x);
    case 'pow':
    case 'binom':
      return writtenDen(t.a, x) || writtenDen(t.b, x);
    case 'fn':
      return writtenDen(t.a, x);
    default:
      return false;
  }
}

/** Le terme vaut-il 0 (forme réduite) ? */
export function isZeroTerm(t: Term): boolean {
  const rf = reducedRf(t);
  return !!rf && polyIsZero(rf.n);
}

/** Facteurs multiplicatifs écrits d'un membre (les nombres et les signes sont laissés). */
export function factorsOf(t: Term, out: Term[] = []): Term[] {
  switch (t.k) {
    case 'num':
      return out;
    case 'mul':
      t.c.forEach((c) => factorsOf(c, out));
      return out;
    case 'div':
      factorsOf(t.a, out);
      factorsOf(t.b, out);
      return out;
    case 'pow':
      if (t.b.k === 'num' && Q.isInteger(t.b.v) && t.b.v.n > 0n) return factorsOf(t.a, out);
      out.push(t);
      return out;
    default:
      out.push(t);
      return out;
  }
}

/** Les facteurs qui contiennent x, et leur degré. */
function xFactors(t: Term, x: string): { f: Term; deg: number }[] | null {
  const out: { f: Term; deg: number }[] = [];
  for (const f of factorsOf(t)) {
    if (!has(f, x)) continue;
    const s = shapeOf(f, x);
    if (!s || s.opaque || s.den) return null;
    out.push({ f, deg: s.deg });
  }
  return out;
}

/** Produit (ou quotient) de facteurs du premier degré : « (x − 2)(x − 3) », « x(2 − x) ». */
export function isFactored(t: Term, x: string): boolean {
  if (t.k === 'add') return false;
  const fs = xFactors(t, x);
  return !!fs && fs.length > 0 && fs.every((f) => f.deg <= 1);
}
/** Produit dont un facteur reste de degré 2 ou plus : « (x − 1)(x² − 4) ». */
function isPartial(t: Term, x: string): boolean {
  if (t.k === 'add') return false;
  const fs = xFactors(t, x);
  return !!fs && fs.length > 1 && fs.some((f) => f.deg >= 2);
}

/** « (A)² » ou « k × (A)² » avec A du premier degré : la base A ; null sinon. */
export function squareBase(t: Term, x: string): { base: Term; coef: boolean } | null {
  const sq = (u: Term): Term | null => {
    if (u.k !== 'pow' || u.b.k !== 'num' || !Q.eq(u.b.v, Q.rat(2))) return null;
    const s = shapeOf(u.a, x);
    return s && s.deg === 1 && !s.den && !s.opaque && occurrences(u.a, x) === 1 ? u.a : null;
  };
  const direct = sq(t);
  if (direct) return { base: direct, coef: false };
  if (t.k === 'mul') {
    const rest = t.c.filter((c) => c.k !== 'num');
    if (rest.length === 1) {
      const b = sq(rest[0]!);
      if (b) return { base: b, coef: true };
    }
  }
  return null;
}
/**
 * « (A)³ » ou « k × (A)³ » avec A du premier degré (moteur v41) : la base A ; null sinon.
 * La fonction cube est strictement croissante : A³ = c a une seule solution.
 */
export function cubeBase(t: Term, x: string): { base: Term; coef: boolean } | null {
  const cu = (u: Term): Term | null => {
    if (u.k !== 'pow' || u.b.k !== 'num' || !Q.eq(u.b.v, Q.rat(3))) return null;
    const s = shapeOf(u.a, x);
    return s && s.deg === 1 && !s.den && !s.opaque && occurrences(u.a, x) === 1 ? u.a : null;
  };
  const direct = cu(t);
  if (direct) return { base: direct, coef: false };
  if (t.k === 'mul') {
    const rest = t.c.filter((c) => c.k !== 'num');
    if (rest.length === 1) {
      const b = cu(rest[0]!);
      if (b) return { base: b, coef: true };
    }
  }
  return null;
}
/** x n'apparaît qu'une fois, et sous un carré : « x² − 4 », « −(x + 2)² + 9 ». */
function underSquareOnce(t: Term, x: string): boolean {
  if (occurrences(t, x) !== 1) return false;
  const walk = (u: Term, inSq: boolean): boolean => {
    switch (u.k) {
      case 'sym':
        return u.n === x && inSq;
      case 'num':
        return false;
      case 'add':
      case 'mul':
        return u.c.some((c) => walk(c, inSq));
      case 'div':
        return walk(u.a, inSq) || walk(u.b, inSq);
      case 'pow':
        return walk(u.a, inSq || (u.b.k === 'num' && Q.eq(u.b.v, Q.rat(2)))) || walk(u.b, inSq);
      case 'fn':
      case 'binom':
        return false;
    }
  };
  return walk(t, false);
}

/** x seul face à une constante (`either` : de l'un ou l'autre côté). */
export function isolatedRel(r: Relation, x: string, either: boolean): boolean {
  if (r.l.k === 'sym' && r.l.n === x && !has(r.r, x)) return true;
  return either && r.r.k === 'sym' && r.r.n === x && !has(r.l, x);
}

/**
 * Forme d'une relation (voir l'en-tête). `either` : x seul à droite compte comme résolu
 * (dans une chaîne « −1 < x » est déjà la réponse).
 */
export function relForm(r: Relation, x: string, either = false): PolyForm {
  if (!has(r.l, x) && !has(r.r, x)) return 'solved';
  if (isolatedRel(r, x, either)) return 'solved';
  const d = shapeOf(sub(r.l, r.r), x);
  if (!d) return 'unknown';
  if (d.opaque) return 'transcendental';
  if (d.deg <= 1 && !d.den) return 'linear';
  const zl = isZeroTerm(r.l);
  const zr = isZeroTerm(r.r);
  const side = zl ? r.r : zr ? r.l : null;
  if (side && isFactored(side, x)) return 'factored';
  // x d'un seul côté, une seule fois, sous un carré
  const lx = has(r.l, x);
  const rx = has(r.r, x);
  const xs = lx && !rx ? r.l : rx && !lx ? r.r : null;
  if (xs && occurrences(xs, x) === 1) {
    if (squareBase(xs, x)) return 'square';
    if (cubeBase(xs, x)) return 'cube';
    if (underSquareOnce(xs, x)) return 'canonical';
  }
  if (d.den && r.rel === '=') return 'rational';
  if (side) return isPartial(side, x) ? 'partial' : 'expanded';
  return 'unsorted';
}

/** Une relation lue dans l'autre sens (« 2 > x » → « x < 2 »). */
export function flipRel(r: Relation): Relation {
  const FLIP: Record<Rel, Rel> = { '=': '=', '<': '>', '>': '<', '≤': '≥', '≥': '≤' };
  return { rel: FLIP[r.rel], l: r.r, r: r.l };
}

/**
 * Distance à la réponse d'une ligne d'une feuille « factor » (voir l'en-tête).
 * `linearH` : l'isolation habituelle d'une relation du premier degré.
 */
export function polyH(s: Statement, x: string, linearH: (r: Relation) => number): number {
  const relH = (r: Relation, either: boolean): number => {
    const f = relForm(r, x, either);
    if (f === 'linear') {
      const h = either ? Math.min(linearH(r), linearH(flipRel(r))) : linearH(r);
      return Math.min(9, h);
    }
    if (f === 'transcendental') return FORM_H.transcendental + linearH(r);
    // x seulement à droite (« 4 ≥ x² ») : un cran de plus, l'échange des membres avance
    const right = !either && !has(r.l, x) && has(r.r, x) ? 1 : 0;
    if (f === 'square') {
      const sq = squareBase(has(r.l, x) ? r.l : r.r, x);
      return FORM_H.square + (sq?.coef ? 2 : 0) + right;
    }
    if (f === 'cube') {
      const cu = cubeBase(has(r.l, x) ? r.l : r.r, x);
      return FORM_H.cube + (cu?.coef ? 2 : 0) + right;
    }
    return FORM_H[f] + (f === 'solved' ? 0 : right);
  };
  if (s.k === 'expr') return 0;
  if (s.k === 'rel') return relH(s, false);
  const clauses = s.k === 'set' ? s.clauses : s.items.map((i) => [i]);
  const hs = clauses.map((c) => Math.max(...c.map((r) => relH(r, true))));
  const max = Math.max(...hs);
  // une clause de plus (« p = 0,3 ou p = 0,7 » quand l'énoncé impose p < 0,5) : un peu plus loin
  return max + 0.1 * (hs.reduce((a, b) => a + b, 0) - max) + 0.05 * (hs.length - 1);
}

/** La forme d'une ligne (une relation seule ; un ensemble : « solved » si résolu). */
export function statementForm(s: Statement, x: string): PolyForm {
  if (s.k === 'rel') return relForm(s, x);
  if (s.k === 'expr') return 'unknown';
  const rels = s.k === 'set' ? s.clauses.flat() : s.items;
  return rels.every((r) => isolatedRel(r, x, true)) ? 'solved' : 'linear';
}

/** Chaque relation : x seul face à une constante (un ensemble résolu, « −1 < x ≤ 2 »). */
export function isSolvedSet(s: Statement, x: string): boolean {
  if (s.k === 'expr') return false;
  const rels = s.k === 'rel' ? [s] : s.k === 'set' ? s.clauses.flat() : s.items;
  return rels.length > 0 && rels.every((r) => isolatedRel(r, x, true));
}

/* ------------------------------ trinôme, Δ, racines ------------------------------ */

export interface Trinomial {
  a: Rat;
  b: Rat;
  c: Rat;
  /** b² − 4ac */
  disc: Rat;
}

/**
 * Le trinôme d'une relation de degré 2 en x, coefficients rationnels : membre gauche
 * moins membre droit (« x² = 2x + 3 » : x² − 2x − 3). null sinon.
 */
export function trinomialOf(r: Relation, x: string): Trinomial | null {
  const rf = reducedRf(sub(r.l, r.r));
  const d = rf ? polyRat(rf.d) : null;
  if (!rf || d === null) return null;
  const coefs = univariateCoefs(rf.n, x);
  if (!coefs || coefs.length !== 3 || Q.isZero(coefs[2]!)) return null;
  const k = Q.div(Q.ONE, d)!;
  const [c, b, a] = coefs.map((v) => Q.mul(v, k)) as [Rat, Rat, Rat];
  const disc = Q.sub(Q.mul(b, b), Q.mul(Q.rat(4), Q.mul(a, c)));
  return { a, b, c, disc };
}

/**
 * Coefficients à simplifier d'une relation développée (« 2x² + 28x − 480 = 0 » : tous
 * pairs ; « n(n + 1) ÷ 2 = 210 » : une fraction) : 1 si l'on peut encore diviser ou
 * multiplier pour obtenir des coefficients entiers premiers entre eux, 0 sinon.
 */
export function coefficientDebt(r: Relation, x: string): number {
  const f = relForm(r, x);
  if (f !== 'expanded' && f !== 'unsorted' && f !== 'partial' && f !== 'canonical') return 0;
  const rf = reducedRf(sub(r.l, r.r));
  const d = rf ? polyRat(rf.d) : null;
  if (!rf || d === null || Q.isZero(d)) return 0;
  const coefs = univariateCoefs(rf.n, x);
  if (!coefs || coefs.length < 3) return 0;
  const k = Q.div(Q.ONE, d)!;
  const cs = coefs.map((v) => Q.mul(v, k)).filter((v) => !Q.isZero(v));
  if (cs.some((v) => !Q.isInteger(v))) return 1;
  const gcd = (a: bigint, b: bigint): bigint => (b === 0n ? a : gcd(b, a % b));
  const g = cs.reduce((acc, v) => gcd(acc, v.n < 0n ? -v.n : v.n), 0n);
  return g > 1n ? 1 : 0;
}

/** Nom d'une grandeur auxiliaire du second degré : Δ, x₁, x₂, x₀ (racine double). */
export type AuxKind = 'disc' | 'root';
export function auxKind(name: string, x: string): AuxKind | null {
  if (name === 'Δ') return 'disc';
  if (name === `${x}₁` || name === `${x}₂` || name === `${x}₀`) return 'root';
  return null;
}
